// Narrow-width inspector sheet (CONTRACT "Shell geometry" under 1024;
// shared.css section 20): a fixed full-height sheet on the right edge between
// the top bar and the status bar, 360 px, full width under 768. No scrim; the
// page stays visible (the docked column is the desktop frame).
//
// This is the inspector REGION at narrow widths, not an overlay (redo-1 P2).
// So: no aria-modal, no focus trap, no outside-press close. F6 region cycling
// leaves it the way it leaves the docked column, and clicking a row behind it
// re-fills it instead of dismissing it. Escape closes, and focus moves into
// the sheet on open so a keyboard user lands in the panel they just asked for.
// No motion: the docked inspector eases its grid column, a fixed sheet has no
// column to ease.
//
// Two keyboard rules the region model still owes the reader, both measured at
// 390x844 in docs/evidence/final:
//   - Tab must not walk into what this sheet paints over. At 390 the sheet is
//     the whole viewport under the top bar, so 84 of 90 stops on Provider
//     Health were focused but invisible (WCAG 2.2 AA 2.4.11). The caller names
//     the covered regions and they are made inert while it is open. The top
//     bar and the status bar are not covered, so they stay reachable.
//   - Closing returns focus to whatever opened the sheet. Escape used to leave
//     document.activeElement on <body>, restarting the next Tab at the top of
//     the document.
// The exported name is kept for its importers (shell/InspectorSlot).
import { useEffect, useId, useRef, type ReactNode } from 'react';
import { X } from '@phosphor-icons/react';
import { Button } from './Button';
import { KeyCap } from './KeyCap';
import { cx } from './cx';
import './BottomSheet.css';

// Stable identity: a fresh array literal per render would re-run the effect
// on every render and thrash the attribute.
const NO_COVERED_REGIONS: readonly string[] = [];

export interface BottomSheetProps {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly title: ReactNode;
  readonly subtitle?: ReactNode;
  // The same identity slot the docked column carries. Without it the same
  // inspector showed the token artwork at 1440 and dropped it at 390.
  readonly artwork?: ReactNode;
  readonly children: ReactNode;
  readonly label?: string;
  readonly className?: string;
  // Selectors for the regions this fixed sheet paints over. Made inert while
  // it is open, so Tab cannot land on a control the reader cannot see. The
  // sheet is a primitive and knows nothing about the shell, so the caller
  // names them. Must be a stable reference.
  readonly coveredRegions?: readonly string[];
}

export function BottomSheet({ open, onClose, title, subtitle, artwork, children, label = 'Inspector', className, coveredRegions = NO_COVERED_REGIONS }: BottomSheetProps) {
  const ref = useRef<HTMLElement>(null);

  const titleId = useId();

  // Declared before the focus effect so its cleanup runs first: the opener has
  // to be focusable again before focus is handed back to it.
  useEffect(() => {
    if (!open || coveredRegions.length === 0) return undefined;
    const covered = coveredRegions
      .flatMap((selector) => Array.from(document.querySelectorAll<HTMLElement>(selector)))
      .filter((node) => !node.hasAttribute('inert'));
    for (const node of covered) node.setAttribute('inert', '');
    return () => {
      for (const node of covered) node.removeAttribute('inert');
    };
  }, [open, coveredRegions]);

  useEffect(() => {
    if (!open) return undefined;
    // <body> is not an opener: pressing "]" opens the sheet from the document
    // itself, and handing focus back to <body> is the very failure this
    // restore exists to stop.
    const active = document.activeElement;
    const opener = active instanceof HTMLElement && active !== document.body ? active : null;
    ref.current?.focus();
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key !== 'Escape') return;
      if (!ref.current?.contains(event.target as Node)) return;
      event.stopPropagation();
      onClose();
    };
    const node = ref.current;
    node?.addEventListener('keydown', onKeyDown);
    return () => {
      node?.removeEventListener('keydown', onKeyDown);
      // Back to the row that opened it, per the contract's Escape rule
      // ("focus stays on the row"). If that element is gone, the selected row
      // and then the workspace itself are the fallbacks.
      const target = opener?.isConnected
        ? opener
        : document.querySelector<HTMLElement>('[data-row-id][aria-selected="true"]')
          ?? document.querySelector<HTMLElement>('main[data-region="work"]');
      target?.focus();
    };
  }, [open, onClose]);

  if (!open) return null;
  return (
    <aside ref={ref} aria-label={label} aria-describedby={titleId} tabIndex={-1} data-region="inspector" className={cx('bsheet', className)}>
      <div className="bsheet-h">
        {artwork ? <span className="insp-art">{artwork}</span> : null}
        <div className="bsheet-t">
          <b id={titleId}>{title}</b>
          {subtitle ? <small>{subtitle}</small> : null}
        </div>
        <div className="bsheet-end">
          <KeyCap>Esc</KeyCap>
          <Button variant="icon" size="sm" aria-label="Close inspector" icon={<X />} onClick={onClose} />
        </div>
      </div>
      <div className="bsheet-b">{children}</div>
    </aside>
  );
}
